import { recalculateLaps, recalculateSessions } from '../calc/boundaries.js'
import type { Activity, ActivityRecord } from '../model/activity.js'
import { computeTiming } from '../model/timing.js'
import { degreesToSemicircles } from '../model/units.js'
import type { Operation, OperationResult, OperationWarning } from './operations.js'

/**
 * Point-level edits for direct map editing (`add-map-point-editing`
 * change, `activity-editing` spec delta):
 *
 * - `moveGpsPoints` — set the position of specific records (one dragged
 *   point or a dragged selection), identified by exact timestamp.
 * - `insertPoint` — insert one new record in a free whole second between
 *   two existing records.
 * - `deleteRecords` — remove specific records (by exact timestamp),
 *   without shifting any other timestamp.
 *
 * Each is its own operation, distinct from the five range-based GPS/
 * segment operations (`removeInterval`, `removeGpsOnly`, `setGpsConstant`,
 * `interpolateGps`, `repairOutlier`) and from each other. Every function
 * here is pure: the input activity and op are never mutated.
 *
 * Invalid input throws an `Error` from `applyOperation`. Validity depends
 * only on the op and the state it is applied to, and history replay is
 * deterministic, so an op that applied cleanly once (e.g. via
 * `previewOperation`) never throws on replay.
 */

export type MoveGpsPointsOperation = Extract<Operation, { type: 'moveGpsPoints' }>
export type InsertPointOperation = Extract<Operation, { type: 'insertPoint' }>
export type DeleteRecordsOperation = Extract<Operation, { type: 'deleteRecords' }>

function time(record: Pick<ActivityRecord, 'timestamp'>): number {
  return record.timestamp instanceof Date ? record.timestamp.getTime() : Number.NaN
}

function assertTimestamp(value: unknown, label: string): asserts value is number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(
      `${label}: timestamp must be a finite number of milliseconds (got ${String(value)}).`,
    )
  }
}

function assertLatLon(lat: unknown, lon: unknown, label: string): void {
  if (typeof lat !== 'number' || !Number.isFinite(lat) || lat < -90 || lat > 90) {
    throw new Error(
      `${label}: latitude must be a number in [-90, 90] degrees (got ${String(lat)}).`,
    )
  }
  if (typeof lon !== 'number' || !Number.isFinite(lon) || lon < -180 || lon > 180) {
    throw new Error(
      `${label}: longitude must be a number in [-180, 180] degrees (got ${String(lon)}).`,
    )
  }
}

function hasGps(r: ActivityRecord): boolean {
  return typeof r.positionLat === 'number' && typeof r.positionLong === 'number'
}

const POSITION_SUMMARY_FIELDS = [
  'startPositionLat',
  'startPositionLong',
  'endPositionLat',
  'endPositionLong',
  'necLat',
  'necLong',
  'swcLat',
  'swcLong',
] as const

function carriesPositionSummary(activity: Activity): boolean {
  return [...activity.laps, ...activity.sessions].some((m) =>
    POSITION_SUMMARY_FIELDS.some((f) => typeof m[f] === 'number'),
  )
}

// ---------------------------------------------------------------------------
// moveGpsPoints
// ---------------------------------------------------------------------------

/**
 * Sets `positionLat`/`positionLong` (degrees -> FIT semicircles) on every
 * record whose timestamp exactly matches a move's `timestampMs` and that
 * already has GPS. Nothing else changes: not time, not any sensor field,
 * not the device-recorded `distance`/`speed`, not laps/sessions/events.
 *
 * - A move matching no record is skipped and counted.
 * - A move matching only records without GPS is skipped and counted
 *   (moving a point never *adds* GPS to a record; use `setGpsConstant`
 *   or `interpolateGps` for that).
 * - If `moves` lists the same timestamp more than once, the last entry
 *   wins.
 */
export function applyMoveGpsPoints(
  activity: Activity,
  op: MoveGpsPointsOperation,
): OperationResult {
  if (!Array.isArray(op.moves) || op.moves.length === 0) {
    throw new Error('moveGpsPoints: at least one move is required.')
  }
  const target = new Map<number, { lat: number; lon: number }>()
  op.moves.forEach((move, i) => {
    assertTimestamp(move.timestampMs, `moveGpsPoints move #${i + 1}`)
    assertLatLon(move.lat, move.lon, `moveGpsPoints move #${i + 1}`)
    target.set(move.timestampMs, {
      lat: degreesToSemicircles(move.lat),
      lon: degreesToSemicircles(move.lon),
    })
  })

  const matched = new Set<number>()
  const movedTimes = new Set<number>()
  let movedRecords = 0
  const records = activity.records.map((r) => {
    const t = time(r)
    const position = target.get(t)
    if (position == null) return r
    matched.add(t)
    if (!hasGps(r)) return r
    movedTimes.add(t)
    movedRecords++
    return { ...r, positionLat: position.lat, positionLong: position.lon }
  })

  const noRecord = [...target.keys()].filter((t) => !matched.has(t)).length
  const noGps = [...matched].filter((t) => !movedTimes.has(t)).length

  const details: string[] = []
  if (noRecord > 0) details.push(`${noRecord} point(s) skipped: no record at that timestamp`)
  if (noGps > 0) details.push(`${noGps} point(s) skipped: the record has no GPS position`)
  let message =
    `Moved the GPS position of ${movedRecords} record(s)` +
    (details.length > 0 ? `; ${details.join('; ')}` : '') +
    '. Device-recorded distance and speed were not changed and still reflect the original track.'
  if (movedRecords > 0 && carriesPositionSummary(activity)) {
    message += ' Lap/session start/end position and bounding-box fields were not updated.'
  }

  return {
    activity: movedRecords > 0 ? { ...activity, records } : activity,
    warnings: [{ message }],
  }
}

// ---------------------------------------------------------------------------
// insertPoint
// ---------------------------------------------------------------------------

export type InsertPointPlan =
  | { ok: true; timestampMs: number; previousTimestampMs: number; nextTimestampMs: number }
  | { ok: false; reason: string }

/**
 * Where `insertPoint` would put a new record after the record at
 * `afterTimestampMs`, or why it can't. FIT record timestamps are whole
 * seconds, so a new record needs a whole second strictly between the
 * record and the next one (a gap of at least 2 s); the chosen second is
 * the one closest to the midpoint (ties round up).
 *
 * If several records share `afterTimestampMs`, the last of them is used.
 */
export function planInsertPoint(activity: Activity, afterTimestampMs: number): InsertPointPlan {
  if (typeof afterTimestampMs !== 'number' || !Number.isFinite(afterTimestampMs)) {
    return { ok: false, reason: 'the timestamp is not a finite number of milliseconds' }
  }
  let index = -1
  for (let i = activity.records.length - 1; i >= 0; i--) {
    const r = activity.records[i]
    if (r != null && time(r) === afterTimestampMs) {
      index = i
      break
    }
  }
  if (index < 0) {
    return { ok: false, reason: 'no record exists at that timestamp' }
  }
  const next = activity.records[index + 1]
  const nextMs = next == null ? Number.NaN : time(next)
  if (next == null || Number.isNaN(nextMs)) {
    return {
      ok: false,
      reason:
        'no free second between these points; it is the last record, so there is no next point',
    }
  }
  const lowSec = Math.floor(afterTimestampMs / 1000) + 1 // first whole second > after
  const highSec = Math.ceil(nextMs / 1000) - 1 // last whole second < next
  if (lowSec > highSec) {
    return {
      ok: false,
      reason: `no free second between these points; they are ${(nextMs - afterTimestampMs) / 1000} s apart and FIT record timestamps are whole seconds (a gap of at least 2 s is needed)`,
    }
  }
  const midSec = Math.round((afterTimestampMs + nextMs) / 2000)
  const sec = Math.min(highSec, Math.max(lowSec, midSec))
  return {
    ok: true,
    timestampMs: sec * 1000,
    previousTimestampMs: afterTimestampMs,
    nextTimestampMs: nextMs,
  }
}

/** Whether `insertPoint` can insert a record after the record at `afterTimestampMs`. */
export function canInsertPointAfter(activity: Activity, afterTimestampMs: number): boolean {
  return planInsertPoint(activity, afterTimestampMs).ok
}

/**
 * Record fields `insertPoint` interpolates, each with the quantization
 * FIT applies on encode (so the in-memory value equals what re-decodes):
 * integer fields are rounded; scaled fields are rounded to their FIT
 * resolution (speed scale 1000, distance scale 100, altitude scale 5
 * offset 500).
 */
const INTERPOLATED_FIELDS: Record<string, (v: number) => number> = {
  heartRate: Math.round,
  cadence: Math.round,
  power: Math.round,
  temperature: Math.round,
  speed: (v) => Math.round(v * 1000) / 1000,
  enhancedSpeed: (v) => Math.round(v * 1000) / 1000,
  distance: (v) => Math.round(v * 100) / 100,
  altitude: (v) => Math.round((v + 500) * 5) / 5 - 500,
  enhancedAltitude: (v) => Math.round((v + 500) * 5) / 5 - 500,
}

/** The interpolated field names `insertPoint` may fill, for documentation/UI use. */
export const INSERT_POINT_INTERPOLATED_FIELDS: readonly string[] = Object.keys(INTERPOLATED_FIELDS)

/**
 * Inserts one new record after the record at `afterTimestampMs`, at the
 * second chosen by `planInsertPoint`, with the given position (degrees).
 *
 * The new record carries only: `timestamp`, `positionLat`/`positionLong`,
 * and each field in `INSERT_POINT_INTERPOLATED_FIELDS` that BOTH
 * neighbours carry as a finite number, linearly interpolated by time.
 * A field only one neighbour has is omitted (never invented or copied);
 * developer fields and any other field are never copied.
 *
 * Laps, sessions and events are left unchanged: the new timestamp is
 * strictly between two existing records, so no lap/session window
 * boundary or elapsed/timer time changes, and interpolated cumulative
 * `distance` keeps lap/session distance spans the same.
 */
export function applyInsertPoint(activity: Activity, op: InsertPointOperation): OperationResult {
  assertTimestamp(op.afterTimestampMs, 'insertPoint')
  assertLatLon(op.lat, op.lon, 'insertPoint')
  const plan = planInsertPoint(activity, op.afterTimestampMs)
  if (!plan.ok) {
    throw new Error(`insertPoint: ${plan.reason}.`)
  }

  const index = activity.records.findLastIndex((r) => time(r) === op.afterTimestampMs)
  const before = activity.records[index] as ActivityRecord
  const after = activity.records[index + 1] as ActivityRecord
  const fraction =
    (plan.timestampMs - plan.previousTimestampMs) /
    (plan.nextTimestampMs - plan.previousTimestampMs)

  const inserted: ActivityRecord = {
    timestamp: new Date(plan.timestampMs),
    positionLat: degreesToSemicircles(op.lat),
    positionLong: degreesToSemicircles(op.lon),
  }
  const interpolatedNames: string[] = []
  for (const [field, quantize] of Object.entries(INTERPOLATED_FIELDS)) {
    const a = before[field]
    const b = after[field]
    if (typeof a !== 'number' || typeof b !== 'number') continue
    if (!Number.isFinite(a) || !Number.isFinite(b)) continue
    inserted[field] = quantize(a + (b - a) * fraction)
    interpolatedNames.push(field)
  }

  const records = [
    ...activity.records.slice(0, index + 1),
    inserted,
    ...activity.records.slice(index + 1),
  ]

  let message =
    `Inserted a new record at ${new Date(plan.timestampMs).toISOString()} with the chosen position` +
    (interpolatedNames.length > 0
      ? `; ${interpolatedNames.join(', ')} were linearly interpolated from the neighbouring records and are estimates, not measurements.`
      : '; no other values were available to interpolate from both neighbouring records.')

  const inPause = computeTiming(activity).pauses.some(
    (p) => plan.timestampMs >= p.start.getTime() && plan.timestampMs < p.end.getTime(),
  )
  if (inPause) {
    message += ' The new record falls inside a timer pause (between a timer stop and start event).'
  }
  const inLap =
    activity.laps.length === 0 ||
    activity.laps.some(
      (l) =>
        l.startTime instanceof Date &&
        l.timestamp instanceof Date &&
        plan.timestampMs >= l.startTime.getTime() &&
        plan.timestampMs <= l.timestamp.getTime(),
    )
  if (!inLap) {
    message += " The new record is not inside any lap's time window."
  }

  return { activity: { ...activity, records }, warnings: [{ message }] }
}

// ---------------------------------------------------------------------------
// deleteRecords
// ---------------------------------------------------------------------------

/**
 * Removes exactly the records whose timestamp is in `timestampsMs` (all
 * records sharing a listed timestamp). Nothing is shifted: every
 * remaining record keeps its absolute timestamp (a gap is left, never a
 * compression). Events are not records and are kept, including any at a
 * deleted record's timestamp.
 *
 * Laps/sessions are recalculated the same way `crop`/`removeInterval` do
 * (`recalculateLaps`/`recalculateSessions`): boundaries and timing follow
 * the retained records, and a lap/session whose record window lost
 * records has its distance/speed/ascent/descent/heart-rate recalculated
 * and calories scaled. A lap left with no records is dropped.
 */
export function applyDeleteRecords(
  activity: Activity,
  op: DeleteRecordsOperation,
): OperationResult {
  if (!Array.isArray(op.timestampsMs) || op.timestampsMs.length === 0) {
    throw new Error('deleteRecords: at least one timestamp is required.')
  }
  op.timestampsMs.forEach((t, i) => assertTimestamp(t, `deleteRecords timestamp #${i + 1}`))
  const targets = new Set(op.timestampsMs)

  const matched = new Set<number>()
  const records = activity.records.filter((r) => {
    const t = time(r)
    if (!targets.has(t)) return true
    matched.add(t)
    return false
  })
  const deleted = activity.records.length - records.length
  const unmatched = targets.size - matched.size

  const warnings: OperationWarning[] = []
  if (deleted === 0) {
    warnings.push({
      message: `Deleted 0 records; ${unmatched} timestamp(s) matched no record.`,
    })
    return { activity, warnings }
  }

  // Events are all kept; `recalculateLaps`/`recalculateSessions` clip
  // them to each interval's narrowed record window themselves, so a kept
  // event at a deleted record's timestamp can't stretch timing.
  const laps = recalculateLaps(activity.laps, activity.records, records, activity.events)
  const sessions = recalculateSessions(
    activity.sessions,
    activity.records,
    records,
    activity.events,
  )

  const details: string[] = []
  if (unmatched > 0) details.push(`${unmatched} timestamp(s) matched no record and were skipped`)
  const droppedLaps = activity.laps.length - laps.length
  if (droppedLaps > 0)
    details.push(`${droppedLaps} lap(s) no longer contained any record and were removed`)
  if (sessions.length < activity.sessions.length) {
    details.push('no records remain, so the session summary was removed')
  }
  warnings.push({
    message:
      `Deleted ${deleted} record(s) without shifting any other timestamp` +
      (details.length > 0 ? `; ${details.join('; ')}` : '') +
      '. Lap/session timing and distance/speed/heart-rate/elevation summaries were recalculated from the remaining records; calories (where present) were scaled as an estimate.',
  })

  return { activity: { ...activity, records, laps, sessions }, warnings }
}
